import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import {
  LANDING_PAGES,
  LANDING_LABELS,
  defaultLandingForRole,
  resolveLandingRoute,
  type LandingPage,
} from '../../lib/landingRoute';

// DASH.C1 (#825) — first-login landing-choice modal. Appears on
// /dashboard?firstLogin=true. Pre-selects the role default; Continue persists
// the choice + has_seen_landing_modal=true via PATCH /me/preferences, then
// routes to the chosen surface. Save failure is non-fatal — we still dismiss +
// route so the user is never trapped in the modal.
export default function LandingModal({
  role,
  lastProjectId,
  onClose,
}: {
  role: string;
  lastProjectId?: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<LandingPage>(() => defaultLandingForRole(role));
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      await apiFetch('/users/me/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ default_landing_page: selected, has_seen_landing_modal: true }),
      });
    } catch {
      // Non-fatal: dismiss + route even if the save failed (settable later in Settings).
    } finally {
      setSaving(false);
      onClose();
      navigate(resolveLandingRoute(selected, lastProjectId ?? null));
    }
  };

  return (
    <div
      data-testid="landing-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Choose your landing page"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', padding: 28, width: 'min(440px, 92vw)',
        boxShadow: 'var(--shadow-lg)',
      }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>
          Where should we take you?
        </h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 18px' }}>
          Pick where BluePrint lands you when you log in. You can change this anytime in Settings.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
          {LANDING_PAGES.map((p) => (
            <label
              key={p}
              data-testid={`landing-option-${p}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                border: `1px solid ${selected === p ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                background: selected === p ? 'rgba(124,58,237,0.06)' : 'transparent',
              }}
            >
              <input type="radio" name="landing" value={p} checked={selected === p} onChange={() => setSelected(p)} />
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)' }}>{LANDING_LABELS[p]}</span>
            </label>
          ))}
        </div>

        <button
          type="button"
          data-testid="landing-continue"
          onClick={() => void handleContinue()}
          disabled={saving}
          style={{
            width: '100%', padding: '10px 16px', fontFamily: 'var(--font-display)',
            fontSize: 14, fontWeight: 600, color: 'white', background: 'var(--primary)',
            border: 'none', borderRadius: 'var(--radius-sm)',
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
